import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject, Optional} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AuthApiService, ChangePasswordRequest, UserActivateRequest} from '@npck/af-frontend-api';
import {BusinessErrorCode, rxSpinner, Spinner, toBusinessOrNull} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PASSWORD_PATTERN} from '../../../domain/constants';
import {LogoutService} from '../../../domain/logout-service';

@Component({
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['change-password-dialog.component.scss'],
    standalone: false
})
export class ChangePasswordDialogComponent {
    form: FormGroup<ChangePasswordForm>;
    Spinner = Spinner;
    showCurrentPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;
    activation: ActivationContext | null;
    readonly maxLength = PASSWORD_MAX_LENGTH;

    constructor(
        private authApiService: AuthApiService,
        private snackbarService: SnackbarService,
        private logoutService: LogoutService,
        private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) data: ChangePasswordDialogData | null,
    ) {
        this.activation = data?.activation ?? null;
        const controls: Record<string, FormControl> = {
            newPassword: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.minLength(PASSWORD_MIN_LENGTH),
                    Validators.maxLength(PASSWORD_MAX_LENGTH),
                    Validators.pattern(PASSWORD_PATTERN),
                ],
            }),
            confirmPassword: new FormControl('', {validators: [Validators.required]}),
        };
        if (!this.activation) {
            controls.currentPassword = new FormControl('', {validators: [Validators.required]});
        }
        this.form = new FormGroup(controls, {validators: this.matchPasswords}) as unknown as FormGroup<ChangePasswordForm>;
    }

    submit(): void {
        if (this.activation) {
            this.activate(this.activation);
            return;
        }
        const {currentPassword, newPassword} = this.form.value;
        const request: ChangePasswordRequest = {currentPassword, newPassword};
        this.authApiService
            .changePassword(request)
            .pipe(rxSpinner('change-password'))
            .subscribe({
                next: () => {
                    this.snackbarService.showSnackBar('i18n_af_passwordChangeSuccess');
                    this.dialogRef.close(true);
                    this.logoutService.logout();
                },
                error: (err: HttpErrorResponse) => {
                    const code = toBusinessOrNull(err)?.error;
                    if (code === BusinessErrorCode.AF_CURRENT_PASSWORD_INVALID) {
                        this.form.controls.currentPassword.setErrors({invalidCurrent: true});
                        this.form.controls.currentPassword.markAsTouched();
                    } else if (code === BusinessErrorCode.AF_PASSWORD_RECOVERY_PASSWORD_VALIDATION_FAILED) {
                        this.rejectNewPassword();
                    } else {
                        this.snackbarService.showSnackBar('i18n_af_passwordChangeError');
                    }
                },
            });
    }

    close(): void {
        this.dialogRef.close(false);
    }

    private activate(activation: ActivationContext): void {
        const {newPassword, confirmPassword} = this.form.value;
        const request: UserActivateRequest = {
            username: activation.username,
            otp: activation.otp,
            newPassword,
            confirmPassword,
        };
        this.authApiService
            .activateUser(request)
            .pipe(rxSpinner('change-password'))
            .subscribe({
                next: () => {
                    this.snackbarService.showSnackBar('i18n_af_passwordChangeSuccess');
                    this.dialogRef.close(true);
                },
                error: (err: HttpErrorResponse) => {
                    const code = toBusinessOrNull(err)?.error;
                    if (code === BusinessErrorCode.AF_CURRENT_PASSWORD_INVALID) {
                        this.snackbarService.showSnackBar('i18n_af_errorCurrentPasswordInvalid');
                    } else if (code === BusinessErrorCode.AF_PASSWORD_RECOVERY_PASSWORD_VALIDATION_FAILED) {
                        this.rejectNewPassword();
                    } else {
                        this.snackbarService.showSnackBar('i18n_af_passwordChangeError');
                    }
                },
            });
    }

    private rejectNewPassword(): void {
        this.form.controls.newPassword.setErrors({passwordHistory: true});
        this.form.controls.newPassword.markAsTouched();
    }

    private matchPasswords(control: AbstractControl): ValidationErrors | null {
        const newPassword = control.get('newPassword')?.value;
        const confirmPassword = control.get('confirmPassword')?.value;
        return newPassword === confirmPassword ? null : {mismatch: true};
    }
}

interface ChangePasswordForm {
    currentPassword?: FormControl<string>;
    newPassword: FormControl<string>;
    confirmPassword: FormControl<string>;
}

export interface ChangePasswordDialogData {
    activation?: ActivationContext;
}

export interface ActivationContext {
    username: string;
    otp: string;
}
